import type { ReactNode } from 'react';
import { useEffect, useState } from 'react';
import { clsx } from 'clsx';

import { CompactText } from '../CompactText';

import classes from './Notice.module.scss';

export type NoticeTone = 'info' | 'success' | 'warning' | 'error';

export interface NoticeProps {
  /** Основной текст: показываем `lines` строк, остальное - по наведению/тапу (CompactText). */
  children: ReactNode;
  title?: ReactNode;
  tone?: NoticeTone;
  /** Кнопка/ссылка действия справа (на узком экране переносится под текст). */
  action?: ReactNode;
  lines?: number;
  className?: string;
  role?: 'status' | 'alert';
  /** Свернуть плашку до одной строки и снова раскрыть. */
  collapsible?: boolean;
  /** Короткая подпись, когда плашка свернута. */
  collapsedLabel?: string;
  /** Если задан, выбор «скрыть» сохранится до конца сессии вкладки. */
  storageKey?: string;
}

/**
 * p8: единая компактная плашка-уведомление (чат, кабинеты): иконка тона, короткий
 * заголовок, текст с раскрытием и действие. Встраивается в поток, не перекрывает поле ввода.
 */
export const Notice = ({
  children,
  title,
  tone = 'info',
  action,
  lines = 3,
  className,
  role = 'status',
  collapsible = false,
  collapsedLabel = 'Уведомление',
  storageKey,
}: NoticeProps) => {
  const [collapsed, setCollapsed] = useState(false);

  useEffect(() => {
    if (!collapsible || !storageKey) return;
    setCollapsed(sessionStorage.getItem(storageKey) === '1');
  }, [collapsible, storageKey]);

  const toggle = () => {
    setCollapsed((prev) => {
      const next = !prev;
      if (storageKey) sessionStorage.setItem(storageKey, next ? '1' : '0');
      return next;
    });
  };

  if (collapsible && collapsed) {
    return (
      <div className={clsx(classes.root, classes.folded, classes[tone], className)} role={role}>
        <p className={classes.foldLabel}>{collapsedLabel}</p>
        <button type='button' className={classes.fold} aria-expanded={false} onClick={toggle}>
          Показать
        </button>
      </div>
    );
  }

  return (
    <div className={clsx(classes.root, classes[tone], className)} role={role}>
      <div className={classes.body}>
        {title ? <strong className={classes.title}>{title}</strong> : null}
        <CompactText as='div' lines={lines} tone={tone} className={classes.text}>
          {children}
        </CompactText>
      </div>
      {action ? <div className={classes.action}>{action}</div> : null}
      {collapsible ? (
        <button type='button' className={classes.fold} aria-expanded onClick={toggle}>
          Скрыть
        </button>
      ) : null}
    </div>
  );
};
